/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * @file SPSSPurchaseAdvisorSelfAssessment.tsx
 * Exports a top-level React component that handles routing, context, and i18n.
 */
import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import 'src/common/style/common.scss';
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { UT30SPSS } from '../constants/default';
import Welcome from './Molecules/Welcome/Welcome';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { Button, Modal } from 'carbon-components-react';
import countryList from '../services/currencies.json';
import {
  useLang,
  LangDefaults,
  determineCountry,
} from 'src/common/hooks/widgets/useLang';
import messages from '../locales/messages';
import { segmentsTracking } from 'src/common/lib/tracker';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import Cookies from 'js-cookie';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { FormModal } from 'src/common/utils/FormModal';
import Thanks from '../components/Molecules/Thanks/Thanks';
import { Landing } from './Molecules/Landing/Landing';
import { Steps } from './Molecules/Steps/step';
import * as DEFAULTS from '../constants/default';
import * as URX_FORM from '../constants/urx-form';
import {
  convertToString,
  createUseTranslation,
} from '../../../common/hooks/widgets/useTranslation';
import { useQradarEDRSelector } from '../lib/redux/hooks';
/**
 * App component.
 *
 * The top-level parent component that defines this application.
 *
 * @param {object} props Component props.
 * @param {Element} props.element The DOM element that contains this widget.
 */
export interface AppProps {
  element: Element;
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const AISolutionsAdvisor = ({ langCode, test, countryToTest }: AppProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [openLanding, setOpenLanding] = useState(false);
  const { bpName, companyId, logo, desc, mssp } = useBPName(UT30SPSS, true);
  const [closeModal, setCloseModal] = useState(false);
  const [openSteps, setOpenSteps] = useState(false);
  const [step, setStep] = useState(1);
  const [, setBpConsentMail] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );

  const AEMMessages = useQradarEDRSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  const DEEPLINKS = {
    client_quotes: `https://www.ibm.com/${localeCode}/watsonx/resources/client-quotes`,
    ingration_page: `https://www.ibm.com/${localeCode}/products/watsonx-orchestrate/integrations`,
    experience_products: `https://www.ibm.com/${localeCode}/products/watsonx-orchestrate/demos`,
  };

  //{Modal parameter}
  const [modal, setModal] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const [pathSelected, setPathSelected] = useState('');
  const [toggleCountry, setToggleCountry] = useState(false);
  const [formID, setFormID] = useState('');

  //{Welcome Page}
  const [localExist, setLocalExist] = useState(false);
  const [optionSelected, setOptionSelected] = useState(0);
  const [environment] = useEnv();
  const cookieURL = useCookiesURL();
  const [cookieContent, setCookieContent] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  const setCookie = () => {
    const mailCheckbox =
      document.querySelector<HTMLInputElement>('#Q_MAIL_CONSENT');
    setBpConsentMail(!!mailCheckbox?.checked); // set false if unchecked or if undefined
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: formID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            cookieContent +
            ' | AI Solutions Advisor-' +
            ' | FormId:' +
            formID,
          Q_ASSET_URL: window.location.href, // ISC - Asset URL initial
          Q_ASSET_NAME: 'AI Solutions Advisor', // ISC - Asset Name
        },
      }),
    );
    console.log('Cookies: ', Cookies.get().urxdcq);
  };

  const contactSales = () => {
    setOpenSteps(false);

    setFormID(URX_FORM.CONTACT_SALES_ID);
    setDrawerOpen(true);
    initUrxForm(
      environment,
      URX_FORM.INSTANCE_ID,
      URX_FORM.CONTACT_SALES_ID,
      urxlang,
      companyId,
    );
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'Contact Sales',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
    setTimeout(
      async () => {
        setDrawerOpen(true);
        localStorage.setItem('showContactForm', 'false');
      },
      bpName ? 1000 : 10,
    );
  };

  const digitalLabor = () => {
    setOpenSteps(false);

    setFormID(URX_FORM.DIGITAL_LABOR_IDC);
    setDrawerOpen(true);
    initUrxForm(
      environment,
      URX_FORM.INSTANCE_ID,
      URX_FORM.DIGITAL_LABOR_IDC,
      urxlang,
      companyId,
    );
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'Digital Labor',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
  };

  const wxOrchestrateTrial = () => {
    setOpenSteps(false);

    setFormID(URX_FORM.TRIAL_WX_ORCHESTRATE);
    setDrawerOpen(true);
    initUrxForm(
      environment,
      URX_FORM.INSTANCE_ID,
      URX_FORM.TRIAL_WX_ORCHESTRATE,
      urxlang,
      companyId,
    );
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'wx Orchestrate Trial',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
  };

  const handleLaunchModalLanding = () => {
    setOpenLanding(true);
    setOpenSteps(false);
    setOptionSelected(0);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'Launch Landing Page',
      location: `Welcome Page`,
      action: '',
      field: '',
    });
  };

  const experienceProducts = () => {
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'Experience Products',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
    setTimeout(async () => {
      window.open(DEEPLINKS.experience_products);
    }, 250);
  };

  const customerQuote = () => {
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'Customer Quote',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
    setTimeout(async () => {
      window.open(DEEPLINKS.client_quotes);
    }, 250);
  };

  const integrations = () => {
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'Integrations',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
    setTimeout(async () => {
      window.open(DEEPLINKS.ingration_page);
    }, 250);
  };

  const wx_ai_Trial = () => {
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: 'wx.ai Trial',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
    setTimeout(async () => {
      window.open(DEFAULTS.wx_ai_trial);
    }, 250);
  };

  const handleLaunchModal = (pSelected: string) => {
    setOpenLanding(false);
    setOpenSteps(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'AI Solutions Advisor',
      object: 'AI Solutions Advisor page',
      resultValue: 'CTA clicked',
      CTA: `Tile Option ${pSelected}`,
      location: `LandingPage`,
      action: '',
      field: '',
    });
  };

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
    setCloseModal(true);
    setStep(6);
  };

  useEffect(() => {
    if (closeModal) {
      setStep(6);
    }
  }, [drawerOpen]);

  useEffect(() => {
    console.log('Changing content');
    pathSelected.length === 0
      ? setCookieContent('')
      : setCookieContent('| pathSelected: ' + pathSelected);
    formID.length > 0 ? setCookie() : '';
  }, [cookieContent, pathSelected, formID]);

  /* Have urx form deault in us-en when on language us page */
  useEffect(() => {
    const fetchedUrxLang = localeCode; // us-en
    setUrxLang(userLocale as IBMLocale); // de-de
    if (fetchedUrxLang !== lang) {
      setUrxLang(fetchedUrxLang as IBMLocale); // default us-en
    }
  });

  return (
    <div
      className="bx--grid ai_solution_advisor widget-styles"
      id="AISolutionAdvisorWidget"
    >
      <Helmet>
        <script type="application/javascript" src={urxScriptLoader}></script>
        <script type="application/javascript" src={urxScript}></script>
      </Helmet>
      {localExist && toggleCountry ? (
        <>
          <div className="bx--row">
            <Button
              onClick={() => {
                segmentsTracking('CTA Clicked', {
                  pageTitle: 'AI Solutions Advisor',
                  object: 'AI Solutions Advisor Page',
                  resultValue: 'CTA clicked',
                  CTA: 'Detected country ' + modalLocalCountry,
                  location: `${pathSelected} Assessment`,
                  action: '',
                  field: '',
                });
                setModal(true);
              }}
            >
              {convertToString(
                useTranslation('modal_accessPageCapitalized', true),
              ) + ` ${modalLocalLanguage}`}
            </Button>
          </div>
          <Modal
            open={modal}
            size="sm"
            preventCloseOnClickOutside={true}
            id="ai-sa-lang-selection"
            onRequestClose={() => {
              setModal(false);
            }}
            modalLabel={
              convertToString(useTranslation('modal_foundCountry', true)) +
              ` ${modalLocalLanguage}. ` +
              convertToString(
                useTranslation('modal_accessPageCapitalized', true),
              ) +
              ` ${modalLocalLanguage} ${modalLocalCurrency}`
            }
            modalHeading={
              convertToString(useTranslation('modal_availableIn', true)) +
              ` ${modalLocalLanguage} ` +
              convertToString(
                useTranslation('modal_localCurrency_text', true),
              ) +
              ` ${modalLocalCurrency} `
            }
            secondaryButtonText={convertToString(
              useTranslation('modal_stayHere', true),
            )}
            primaryButtonText={convertToString(
              useTranslation('modal_takeMeThere', true),
            )}
            onRequestSubmit={() => {
              localStorage.setItem('langLocale', selectedLocale);
              (window.location.href =
                'https://www.ibm.com/' +
                selectedLocale +
                '/products/watsonx-orchestrate/ai-assistant-builder/#AISolutionAdvisorWidget'),
                segmentsTracking('CTA Clicked', {
                  pageTitle: 'AI Solutions Advisor',
                  object: 'AI Solutions Advisor Page',
                  resultValue: 'CTA clicked',
                  CTA: 'Switched to country ' + modalLocalCountry,
                  location: `${pathSelected} Assessment`,
                  action: '',
                  field: '',
                });
              return true;
            }}
            onSecondarySubmit={() => {
              segmentsTracking('CTA Clicked', {
                pageTitle: 'AI Solutions Advisor',
                object: 'AI Solutions Advisor Page',
                resultValue: 'CTA clicked',
                CTA: 'Stay here button clicked ' + modalLocalCountry,
                location: `${pathSelected} Assessment`,
                action: '',
                field: '',
              });
              setModal(false);
              setToggleCountry(false);
              return true;
            }}
          ></Modal>
        </>
      ) : null}
      {!openSteps && !openLanding && (
        <Welcome
          handleLaunchModal={handleLaunchModalLanding}
          modal2={modal}
          setModal2={setModal}
          modalBtnLangText={modalLocalLanguage}
          localExist={localExist}
          toggleCountry={toggleCountry}
          flag={false} // to enable/disable globalization
        />
      )}
      <Landing
        openLanding={openLanding}
        setOpenLanding={setOpenLanding}
        handleLaunchModal={handleLaunchModal}
        setOptionSelected={setOptionSelected}
        setStep={setStep}
        setPathSelected={setPathSelected}
      ></Landing>
      <Steps
        handleLaunchModal={handleLaunchModalLanding}
        experienceProducts={experienceProducts}
        customerQuote={customerQuote}
        integrations={integrations}
        wxaiTrial={wx_ai_Trial}
        contactSales={contactSales}
        digitalLabor={digitalLabor}
        wxOrchestrateTrial={wxOrchestrateTrial}
        openSteps={openSteps}
        setOpenSteps={setOpenSteps}
        optionSelected={optionSelected}
        setOptionSelected={setOptionSelected}
        setStep={setStep}
        stepcompleted={step}
        setDrawOpen={setDrawerOpen}
        setFormID={setFormID}
        pathSelected={pathSelected}
        setPathSelected={setPathSelected}
      />

      <FormModal
        pageTitle="AI Solutions Advisor"
        object="AI Solutions Advisor Page"
        productTitle={'AI Solutionns Advisor'}
        pageUrl={''}
        location={'Assessment URX Form'}
        bpName={bpName}
        logo={logo}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        closeModal={closeModal}
        setCloseModal={setCloseModal}
        formId={formID}
      />
      {formComplete && <Thanks bpName={bpName} />}
    </div>
  );
};

export default AISolutionsAdvisor;
